"use client"

import { useState, type ReactNode } from "react"
import { ChevronDown } from "lucide-react"
import {
  PRODUCT_ACCORDION_SECTIONS,
  PRODUCT_ACCORDION_DELIVERY_LINK_SECTIONS,
} from "@constants/product-page"
import type {
  AccordionContentItem,
  AccordionTextSegment,
} from "@constants/product-page"
import type { ProductCustomFields } from "@lib/data/products"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import ProductQA from "@modules/products/components/product-qa"

/** Static — UKSS only sells new stock; there is no per-product condition field. */
const ITEM_CONDITION_LABEL = "Brand New With Tags"

type ProductAccordionsProps = {
  /** Product description for the "About" section */
  description?: string | null
  /** Football-specific attributes (manufacturer, season, colour, etc.) for the spec list */
  customFields?: ProductCustomFields | null
  /** Size option values (e.g. "Small Adults") for the "Available Sizes" spec row */
  availableSizes?: string[]
}

export default function ProductAccordions({
  description,
  customFields,
  availableSizes,
}: ProductAccordionsProps) {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }

  const specItems = buildSpecItems(customFields, availableSizes)

  const sections = Object.entries(PRODUCT_ACCORDION_SECTIONS).map(
    ([key, section]) => ({
      id: key,
      title: section.title,
      content:
        key === "description"
          ? renderDescriptionContent(description, specItems)
          : key === "reviews"
          ? renderPlaceholder("No Reviews")
          : key === "qa"
          ? <ProductQA />
          : (
            <>
              {renderSectionContent(section.content)}
              {PRODUCT_ACCORDION_DELIVERY_LINK_SECTIONS.includes(key) && (
                <p className="mt-3">
                  <LocalizedClientLink
                    href="/shipping"
                    className="text-[15px] font-semibold text-[#007bc4] hover:underline"
                  >
                    Click here for full Delivery Info
                  </LocalizedClientLink>
                </p>
              )}
            </>
          ),
    })
  )

  return (
    <div
      id="product-accordions"
      className="divide-y divide-gray-200 border-t border-gray-200"
    >
      {sections.map((section) => {
        const isOpen = openSections.has(section.id)
        return (
          <div key={section.id}>
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between py-5 outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2 rounded"
              aria-expanded={isOpen}
              aria-controls={`accordion-panel-${section.id}`}
            >
              <span className="text-base font-bold text-gray-900">
                {section.title}
              </span>
              <ChevronDown
                className={`w-5 h-5 flex-shrink-0 text-gray-900 transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </button>
            {isOpen && (
              <div
                id={`accordion-panel-${section.id}`}
                role="region"
                aria-label={section.title}
                className="pb-5 text-sm text-gray-600 leading-relaxed space-y-2"
              >
                {section.content}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function renderAboutContent(description?: string | null) {
  if (!description) {
    return <p>No description available.</p>
  }
  return <p className="whitespace-pre-line">{description}</p>
}

/**
 * Builds the reference site's product spec rows (Item Condition, Suitable For,
 * Available Sizes, Sleeve Length, Colour, Team Name, Season, Manufacturer)
 * from the product's custom fields and Size option values.
 */
function buildSpecItems(
  customFields?: ProductCustomFields | null,
  availableSizes?: string[]
): AccordionContentItem[] {
  if (!customFields) {
    return []
  }

  const plainRows: { label: string; value: string | null }[] = [
    { label: "Item Condition", value: ITEM_CONDITION_LABEL },
    { label: "Suitable For", value: customFields.age_group },
  ]

  const availableSizesRow: AccordionContentItem[] =
    availableSizes && availableSizes.length > 0
      ? [
          {
            type: "list",
            question: "Available Sizes",
            answer: [],
            variant: "tags",
            items: availableSizes.map((size) => [{ text: size }]),
          },
        ]
      : []

  const remainingRows: { label: string; value: string | null }[] = [
    { label: "Sleeve Length", value: customFields.sleeve_length },
    { label: "Colour", value: customFields.color },
    { label: "Team Name", value: customFields.team },
    { label: "Season", value: customFields.season },
    { label: "Manufacturer", value: customFields.manufacturer },
  ]

  const toListItem = (row: { label: string; value: string | null }) => ({
    type: "list" as const,
    question: row.label,
    answer: [],
    items: [[{ text: row.value as string }]],
  })

  return [
    ...plainRows.filter((row) => row.value).map(toListItem),
    ...availableSizesRow,
    ...remainingRows.filter((row) => row.value).map(toListItem),
  ]
}

function renderDescriptionContent(
  description: string | null | undefined,
  specItems: AccordionContentItem[]
) {
  return (
    <>
      {renderAboutContent(description)}
      {specItems.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          {renderSectionContent(specItems, "space-y-2")}
        </div>
      )}
    </>
  )
}

function renderPlaceholder(text: string) {
  return <p className="text-[15px] font-bold text-[#014378]">{text}</p>
}

function renderSectionContent(
  content: AccordionContentItem[],
  gapClassName = "space-y-3"
) {
  const blocks: ReactNode[] = []
  let termBuffer: AccordionContentItem[] = []

  const flushTerms = () => {
    if (termBuffer.length === 0) {
      return
    }
    blocks.push(
      <dl key={`dl-${blocks.length}`} className="space-y-3">
        {termBuffer.map((item, i) => (
          <div key={i}>
            <dt className="font-semibold text-gray-900">{item.question}</dt>
            <dd>{renderRichText(item.answer)}</dd>
          </div>
        ))}
      </dl>
    )
    termBuffer = []
  }

  content.forEach((item, index) => {
    if (item.type === "term") {
      termBuffer.push(item)
      return
    }
    flushTerms()
    if (item.type === "heading") {
      blocks.push(
        <div key={index}>
          <p className="text-base font-bold text-gray-900 mb-1">
            {item.question}
          </p>
          <p>{renderRichText(item.answer)}</p>
        </div>
      )
    } else if (item.type === "list") {
      blocks.push(
        <div key={index}>
          {item.question && (
            <p className="text-[14px] font-bold uppercase text-[#014378] mb-1">
              {item.question}
            </p>
          )}
          {item.variant === "tags" ? (
            <ul className="flex flex-wrap gap-1.5">
              {(item.items ?? []).map((segments, i) => (
                <li
                  key={i}
                  className="bg-[#eef4fb] border border-[#c2d9ef] rounded text-[12px] font-semibold text-[#014378] px-2.5 py-[3px]"
                >
                  {renderRichText(segments)}
                </li>
              ))}
            </ul>
          ) : (
            <ul className="list-none">
              {(item.items ?? []).map((segments, i) => (
                <li key={i}>{renderRichText(segments)}</li>
              ))}
            </ul>
          )}
        </div>
      )
    } else {
      blocks.push(<p key={index}>{renderRichText(item.answer)}</p>)
    }
  })
  flushTerms()

  return <div className={gapClassName}>{blocks}</div>
}

function renderRichText(segments: AccordionTextSegment[]) {
  return segments.map((segment, index) =>
    segment.emphasis === "bold" ? (
      <strong key={index}>{segment.text}</strong>
    ) : segment.emphasis === "italic" ? (
      <em key={index}>{segment.text}</em>
    ) : (
      segment.text
    )
  )
}
